import { CalendarDate, parseDate, today } from '@internationalized/date';
import * as stylex from '@stylexjs/stylex';
import { useForm } from '@tanstack/react-form';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  CatchBoundary,
  createFileRoute,
  notFound,
  redirect,
  useCanGoBack,
} from '@tanstack/react-router';
import { useId } from 'react';
import { Button as CoreButton, TooltipTrigger, type DateValue } from 'react-aria-components';
import {
  isDefined as rIsDefined,
  omitBy as rOmitBy,
  forEachObj as rForEachObj,
  isDeepEqual as rIsDeepEqual,
  pick as rPick,
  isNot as rIsNot,
} from 'remeda';
import {
  check as vCheck,
  maxLength as vMaxLength,
  minValue as vMinValue,
  nonEmpty as vNonEmpty,
  number as vNumber,
  optional as vOptional,
  parse as vParse,
  pipe as vPipe,
  string as vString,
  transform as vTransform,
} from 'valibot';

import { SecondaryActionsIconButton } from '@/components/secondary-actions-icon-button.component';
import { LOCALE } from '@/constants/locale';
import { SESSION_STORAGE } from '@/constants/storage';
import { useFormDraft } from '@/hooks/use-form-draft.hook';
import {
  CampaignRequestDistributionEnum,
  CampaignRequestEditStatusEnum,
  CampaignRequestStatusEnum,
  HttpException,
  type components,
} from '@/libs/openapi';
import {
  Button,
  Calendar,
  CalendarCell,
  CalendarGrid,
  CalendarGridBody,
  CalendarGridHeader,
  CalendarHeader,
  CalendarHeaderCell,
  CALENDAR_SLOTS,
  DateInput,
  DatePicker,
  EditStrokeIcon,
  Form,
  FORM_SLOTS,
  HelpCircleStrokeIcon,
  Hint,
  Icon,
  IconButton,
  Input,
  Label,
  minDelay,
  NumberField,
  Popover,
  PROGRESS_INDICATOR_MIN_DELAY_DURATION,
  SegmentedButton,
  SegmentedButtonGroup,
  Snackbar,
  SNACKBAR_SLOTS,
  Text,
  TextArea,
  TextButton,
  TextField,
  TOAST_TIMEOUT,
  Tooltip,
  tryCatch,
  View,
  Breadcrumbs,
  Breadcrumb,
  PauseCircleSrokeStrokeIcon,
} from '@/libs/serenity-ui';
import { RouterLink, RouterMenuItemLink } from '@/libs/tanstack-router';
import { auditLogQueries } from '@/resources/audit-log/audit-log.api';
import { campaignEditRequestMutations, campaignEditRequestQueries } from '@/resources/campaign-edit-request/campaign-edit-request.api';
import { toApiCampaignEditRequestFieldAdapter } from '@/resources/campaign-edit-request/campaign-edit-request.field-adapter';
import { campaignEditRequestPolicies } from '@/resources/campaign-edit-request/campaign-edit-request.policies';
import { campaignRequestQueries } from '@/resources/campaign-request/campaign-request.api';
import { campaignRequestCommentPolicies } from '@/resources/campaign-request-comment/campaign-request-comment.policies';
import { CampaignRequestChatWidget } from '@/routes/_main/-components/campaign-request-chat-widget.component';
import { Resource } from '@/types/resource.type';
import { toLocalDate } from '@/utils/convert.util';
import { formatNumber } from '@/utils/format.util';
import { timezone } from '@/utils/timezone.util';

type EditRequestType = 'edit' | 'pause';

interface EditRequestFormValues {
  type: EditRequestType;
  addBudget: number | undefined;
  endDate: string | undefined;
  monthlyBudget: Record<string, number>;
  pauseReason: string;
}

export const Route = createFileRoute('/_main/_form/campaigns/$campaignId/edit/request')({
  component: RouteComponent,
  params: {
    parse: ({ campaignId }) => ({ campaignId: vParse(vPipe(vString(), vTransform(Number), vNumber()), campaignId) }),
    stringify: ({ campaignId }) => ({ campaignId: String(campaignId) }),
  },
  beforeLoad: async ({ context, params }) => {
    if (typeof (params.campaignId as unknown) !== 'number') {
      throw notFound({ routeId: '__root__' });
    }

    if (context.user && !context.can.campaignRequests.view()) {
      throw redirect({ to: '/' });
    }

    const details = await context.queryClient.ensureQueryData(campaignRequestQueries.detail(params.campaignId));

    return {
      queryOptions: {
        campaignRequestDetail: campaignRequestQueries.detail(params.campaignId),
        campaignEditRequestList: campaignEditRequestQueries.list(
          params.campaignId,
          {
            filter: { status: [ CampaignRequestEditStatusEnum.IN_REVIEW ] },
            campaignTimezone: details.timezone ?? timezone.get(),
          },
        ),
      },
      can: {
        [Resource.campaignEditRequest as const]: rPick(campaignEditRequestPolicies.bindContext({ user: context.user.original, organization: context.organization?.original }), [ 'create', 'update' ]),
        [Resource.campaignRequestComment as const]: rPick(campaignRequestCommentPolicies.bindContext({ user: context.user.original, organization: context.organization?.original }), [ 'create' ]),
      },
    };
  },
  loader: async ({ context, params }) => {
    if (!context.user) {
      return;
    }

    const [ campaignRequestDetailData, campaignEditRequestListData ] = await Promise.all([
      context.queryClient.ensureQueryData(context.queryOptions.campaignRequestDetail),
      context.queryClient.ensureQueryData(context.queryOptions.campaignEditRequestList),
    ]);

    const isEditableStatus =
      campaignRequestDetailData.status === CampaignRequestStatusEnum.ACTIVE ||
      campaignRequestDetailData.status === CampaignRequestStatusEnum.COMPLETED;

    if (!isEditableStatus) {
      throw redirect({ to: '/campaigns/$campaignId', params: { campaignId: params.campaignId }});
    }

    const hasActiveEditRequests = campaignEditRequestListData.items.length > 0;

    if (hasActiveEditRequests) {
      if (!context.can.campaignEditRequest.update(campaignEditRequestListData.items.at(0)!)) {
        throw redirect({ to: '/campaigns/$campaignId', params: { campaignId: params.campaignId }});
      }
    } else {
      if (!context.can.campaignEditRequest.create({ campaignRequest: campaignRequestDetailData, editRequests: campaignEditRequestListData.items })) {
        throw redirect({ to: '/campaigns/$campaignId', params: { campaignId: params.campaignId }});
      }
    }
  },
  staticData: {
    form: {
      heading: 'Request edit',
      toolbarLayout: 'full-width',
      slots: {
        topBarComponent: TopBar,
        actionsComponent: ActionsComponent,
      },
    },
  },
  head: ({ params }) => ({
    meta: [
      {
        title: `TrendMD | Request campaign ${params.campaignId} edit`,
      },
    ],
  }),
});

function RouteComponent() {
  const id = useId();
  const navigate = Route.useNavigate();
  const routeContext = Route.useRouteContext();
  const routeParams = Route.useParams();
  const canGoBack = useCanGoBack();

  const campaignQuery = useQuery(routeContext.queryOptions.campaignRequestDetail);

  const editRequestsQuery = useQuery(routeContext.queryOptions.campaignEditRequestList);

  const createMutation = useMutation(campaignEditRequestMutations.create);
  const updateMutation = useMutation(campaignEditRequestMutations.update);
  const cancelMutation = useMutation(campaignEditRequestMutations.cancel);

  const inReviewEditRequest = editRequestsQuery.data?.data[0];

  const currentBudget = campaignQuery.data?.transformed.forForm?.totalBudget ?? 0;
  const campaignTimezone = campaignQuery.data?.original?.timezone ?? timezone.get();
  const currentEndDate = campaignQuery.data?.meta.endDateRelativeToTimezone?.forFormValue;
  const isCustomMonthly = campaignQuery.data?.original?.distribution === CampaignRequestDistributionEnum.MONTHLY;

  const cost = campaignQuery.data?.transformed?.forForm?.cost ?? 0;

  const form = useForm({
    defaultValues: {
      type: inReviewEditRequest?.original.campaignStatusPaused ? 'pause' : 'edit',
      addBudget: inReviewEditRequest?.transformed.forForm.newTotalBudget != null
        ? inReviewEditRequest.transformed.forForm.newTotalBudget - currentBudget
        : undefined,
      endDate: inReviewEditRequest?.original.newEndDate
        ? toLocalDate(inReviewEditRequest.original.newEndDate, campaignTimezone)
        : currentEndDate,
      monthlyBudget: inReviewEditRequest?.transformed.forForm.newMonthlyBudget
        ?? campaignQuery.data?.transformed.forForm?.monthlyBudget
        ?? {},
      pauseReason: inReviewEditRequest?.original.pauseCampaignReason ?? '',
    } as EditRequestFormValues,
    onSubmit: async ({ value }) => {
      const newTotal = currentBudget + (value.addBudget ?? 0);

      if (isCustomMonthly && value.type === 'edit') {
        const monthlyBudgetSum = Object.values(value.monthlyBudget).reduce((acc, v) => acc + v, 0);
        if (monthlyBudgetSum !== newTotal) {
          return;
        }
      }

      let body: components['schemas']['CampaignRequestEditDto'];

      if (value.type === 'pause') {
        body = {
          pauseCampaign: true,
          pauseCampaignReason: value.pauseReason || undefined,
        };
      } else {
        const newTotalBudget = newTotal !== currentBudget ? newTotal : undefined;
        const newEndDate = value.endDate !== currentEndDate && value.endDate
          ? value.endDate
          : undefined;
        const newMonthlyBudget = isCustomMonthly && !rIsDeepEqual(value.monthlyBudget, campaignQuery.data?.transformed.forForm?.monthlyBudget ?? {})
          ? value.monthlyBudget
          : undefined;

        body = rOmitBy(toApiCampaignEditRequestFieldAdapter.transform({ newTotalBudget, newMonthlyBudget, newEndDate }), rIsNot(rIsDefined)) as components['schemas']['CampaignRequestEditDto'];
      }

      const mutation = inReviewEditRequest
        ? updateMutation.mutateAsync({ id: inReviewEditRequest.original.id, body }).then(() => inReviewEditRequest.original.id)
        : createMutation.mutateAsync({ id: routeParams.campaignId, body }).then((data) => data?.id);

      const response = await tryCatch(minDelay(mutation, PROGRESS_INDICATOR_MIN_DELAY_DURATION));

      if (response.error) {
        const isProcessingConflict =
          response.error instanceof HttpException &&
          response.error.response?.status === 400;

        if (isProcessingConflict) {
          routeContext.toastQueue.add(
            {
              message: "We're currently processing your existing edit request. You'll be able to submit a new edit request after the current request is reviewed.",
              variant: 'info',
            },
            { timeout: TOAST_TIMEOUT },
          );
        } else {
          routeContext.toastQueue.add(
            { message: response.error.message, variant: 'error', actionProps: { children: 'Retry', onPress: () => form.handleSubmit() }},
            { timeout: TOAST_TIMEOUT },
          );
        }
        return;
      }

      routeContext.queryClient.invalidateQueries(campaignEditRequestQueries.all);
      routeContext.queryClient.invalidateQueries(campaignRequestQueries.all);
      routeContext.queryClient.invalidateQueries(auditLogQueries.all);

      routeContext.toastQueue.add(
        { message: inReviewEditRequest ? 'Edit request updated.' : 'Edit request submitted.', variant: 'success' },
        { timeout: TOAST_TIMEOUT },
      );

      clearDraft();

      const newRecordId = response.data;
      if (newRecordId != null) {
        sessionStorage.setItem(
          SESSION_STORAGE.campaignEditRequestNewRecord(String(routeParams.campaignId)).key,
          String(newRecordId),
        );
      }

      if (canGoBack) {
        navigate({ to: '..', params: { campaignId: routeParams.campaignId }});
      } else {
        navigate({ to: '/campaigns/$campaignId', params: { campaignId: routeParams.campaignId }});
      }
    },
  });

  const { hasDraft, restoreDraft, clearDraft, cancelDraft } = useFormDraft<EditRequestFormValues>({
    storageKey: SESSION_STORAGE.campaignEditRequest(String(routeParams.campaignId)).key,
    subscribe: (cb) => form.store.subscribe(({ currentVal }) => !currentVal.isDefaultValue && cb(currentVal.values)),
    restoreValues: rForEachObj((value, key) => form.setFieldValue(key, value)),
  });

  const typeLabel = `${id}-type-label`;

  return (
    <>
      <CatchBoundary errorComponent={() => null} getResetKey={() => 'reset'} onCatch={(error, errorInfo) => console.error(error, errorInfo)}>
        <CampaignRequestChatWidget
          campaignRequestId={routeParams.campaignId}
          canCreate={routeContext.can.campaignRequestComment.create()}
        />
      </CatchBoundary>

      <Form
        style={pageStyles.base}
        onSubmit={async (e) => {
          e.preventDefault();
          e.stopPropagation();
          await form.handleSubmit();
        }}>
        {(renderProps) => (
          <>
            <form.Subscribe selector={(state) => state.isDirty}>
              {(isDirty) => hasDraft && !isDirty && (
                <Snackbar style={renderProps.fullWidthStyle} variant="warning">
                  <Icon />
                  <Text>Unfinished edit request</Text>
                  <TextButton onPress={() => {
                    form.reset();
                    restoreDraft();
                  }}>
                  Restore
                  </TextButton>
                  <IconButton slot={SNACKBAR_SLOTS.CLOSE} onPress={cancelDraft} />
                </Snackbar>
              )}
            </form.Subscribe>

            <form.Field name="type">
              {(field) => (
                <View style={renderProps.fieldStyles}>
                  <Text color={renderProps.labelColor} id={typeLabel}>Type</Text>

                  <SegmentedButtonGroup
                    aria-labelledby={typeLabel}
                    disallowEmptySelection
                    selectedKeys={new Set([ field.state.value ])}
                    selectionMode="single"
                    size={40}
                    style={segmentedButtonStyles.base}
                    onSelectionChange={(keys) => {
                      const selected = [ ...keys ];
                      if (selected.length > 0) {
                        const newType = selected[0] as EditRequestType;
                        field.handleChange(newType);

                        if (newType === 'pause') {
                          form.setFieldValue('addBudget', undefined);
                          form.setFieldValue('endDate', currentEndDate);
                          form.setFieldValue('monthlyBudget', campaignQuery.data?.transformed.forForm?.monthlyBudget ?? {});
                          form.setFieldMeta('addBudget', (prev) => ({ ...prev, isTouched: false, isBlurred: false, errors: [], errorMap: {}}));
                          form.setFieldMeta('endDate', (prev) => ({ ...prev, isTouched: false, isBlurred: false, errors: [], errorMap: {}}));
                          form.setFieldMeta('monthlyBudget', (prev) => ({ ...prev, isTouched: false, isBlurred: false, errors: [], errorMap: {}}));
                        } else {
                          form.setFieldValue('pauseReason', '');
                          form.setFieldMeta('pauseReason', (prev) => ({ ...prev, isTouched: false, isBlurred: false, errors: [], errorMap: {}}));
                        }
                      }
                    }}>
                    <SegmentedButton id="edit">
                      <EditStrokeIcon />
                      <Text>Edit</Text>
                    </SegmentedButton>
                    <SegmentedButton id="pause">
                      <PauseCircleSrokeStrokeIcon />
                      <Text>Pause</Text>
                    </SegmentedButton>
                  </SegmentedButtonGroup>
                </View>
              )}
            </form.Field>

            <form.Subscribe selector={(state) => state.values.type}>
              {(selectedType) => (
                <>
                  {selectedType === 'edit' && (
                    <>
                      <form.Field
                        name="addBudget"
                        validators={{
                          onBlur: vOptional(vPipe(
                            vNumber(),
                            vMinValue(-currentBudget + 0.01, 'Total budget must be greater than zero'),
                          )),
                        }}>
                        {(field) => {
                          const totalBudget = currentBudget + (field.state.value || 0);

                          return (
                            <NumberField
                              autoFocus
                              id={field.name}
                              isInvalid={field.state.meta.errors.length > 0}
                              name={field.name}
                              size={48}
                              style={renderProps.fieldStyles}
                              value={field.state.value}
                              onBlur={field.handleBlur}
                              onChange={field.handleChange}>
                              <Label color={renderProps.labelColor}>Budget adjustment</Label>

                              <View style={renderProps.rowViewStyle}>
                                <Input placeholder="0" style={renderProps.narrowInputStyle}>
                                  {(inputRenderProps) => (
                                    <>
                                      {inputRenderProps.defaultChildren}
                                      <Text color="disabled">USD</Text>
                                    </>
                                  )}
                                </Input>
                                <Hint style={renderProps.underFieldStyle}>
                                  <Text color="quaternary" size={14} slot={null!}>{formatNumber(totalBudget, 'currency')} USD budget</Text>
                                </Hint>
                              </View>
                              {field.state.meta.errors.length > 0 && (
                                <Hint style={renderProps.underFieldStyle}>
                                  <Icon />
                                  {field.state.meta.errors.at(0)?.message}
                                </Hint>
                              )}
                            </NumberField>
                          );
                        }}
                      </form.Field>

                      <form.Field
                        name="endDate"
                        validators={{
                          onBlur: vOptional(vPipe(
                            vString(),
                            vCheck((value) => {
                              const selected = parseDate(value);
                              const tomorrow = today(campaignTimezone).add({ days: 1 });
                              return selected.compare(tomorrow) >= 0;
                            }, 'End date must be in the future'),
                          )),
                        }}>
                        {(field) => (
                          <DatePicker
                            granularity="day"
                            id={field.name}
                            isDateUnavailable={(date) => date.toString().substring(0, 10) <= today(campaignTimezone).toString()}
                            isInvalid={field.state.meta.errors.length > 0}
                            minValue={today(campaignTimezone).add({ days: 1 }) as unknown as DateValue}
                            placeholderValue={today(campaignTimezone) as unknown as DateValue}
                            style={renderProps.fieldStyles}
                            value={field.state.value ? parseDate(field.state.value) as unknown as DateValue : null}
                            onBlur={field.handleBlur}
                            onChange={(value) => {
                              if (!value) {
                                field.handleChange(undefined);
                                return;
                              }
                              field.handleChange(value.toString().substring(0, 10));
                            }}>
                            <Label color={renderProps.labelColor} style={labelWithHelperButtonStyles.base}>
                              End date
                              <TooltipTrigger>
                                <CoreButton slot={null!} {...stylex.props(helperButtonStyles.base)}>
                                  <HelpCircleStrokeIcon color="quaternary" size={16} />
                                </CoreButton>
                                <Tooltip>
                                  {timezone.getKey(campaignTimezone) ?? campaignTimezone}
                                </Tooltip>
                              </TooltipTrigger>
                            </Label>
                            <DateInput size={48} style={renderProps.narrowInputStyle} />
                            {field.state.meta.errors.length > 0 && (
                              <Hint style={renderProps.underFieldStyle}>
                                <Icon />
                                {field.state.meta.errors.at(0)?.message}
                              </Hint>
                            )}
                            <Popover>
                              <Calendar>
                                <View slot={CALENDAR_SLOTS.CALENDARS_VIEW}>
                                  <CalendarHeader isSmall />
                                </View>
                                <View slot={CALENDAR_SLOTS.CALENDARS_VIEW}>
                                  <CalendarGrid weekdayStyle="short">
                                    <CalendarGridHeader>
                                      {(day) => <CalendarHeaderCell isSmall>{day}</CalendarHeaderCell>}
                                    </CalendarGridHeader>
                                    <CalendarGridBody>
                                      {(date) => <CalendarCell date={date} isSmall />}
                                    </CalendarGridBody>
                                  </CalendarGrid>
                                </View>
                              </Calendar>
                            </Popover>
                          </DatePicker>
                        )}
                      </form.Field>

                      {isCustomMonthly && campaignQuery.data?.original?.startDate && (
                        <form.Subscribe selector={(state) => state.values.endDate}>
                          {(endDate) => {
                            if (!endDate) {
                              return null;
                            }

                            const months: string[] = [];
                            const startDateValue = campaignQuery.data.meta.startDateRelativeToTimezone?.forFormValue;
                            if (!startDateValue) {
                              return null;
                            }
                            const start = parseDate(startDateValue);
                            const end = parseDate(endDate);
                            let current = start.set({ day: 1 });
                            while (current.compare(end) <= 0) {
                              months.push(`${current.year}-${String(current.month).padStart(2, '0')}`);
                              current = current.add({ months: 1 });
                            }

                            if (months.length === 0) {
                              return null;
                            }

                            return (
                              <form.Field
                                name="monthlyBudget"
                                validators={{
                                  onChangeListenTo: [ 'addBudget' ],
                                  onChange: ({ value, fieldApi }) => {
                                    const enteredValue = fieldApi.form.getFieldValue('addBudget') ?? 0;
                                    const newTotal = currentBudget + enteredValue;
                                    const sum = Object.values(value).reduce((acc, v) => acc + v, 0);
                                    if (sum > newTotal) {
                                      return { message: 'Monthly totals exceed the campaign budget' };
                                    }
                                    return undefined;
                                  },
                                }}>
                                {(field) => {
                                  const lastFilledMonth = [ ...months ].reverse().find((m) => (field.state.value[m] ?? 0) > 0);
                                  const errorAfterMonth = lastFilledMonth ?? months[months.length - 1];

                                  const now = today(campaignTimezone);
                                  const currentMonth = `${now.year}-${String(now.month).padStart(2, '0')}`;

                                  return months.map((month) => {
                                    const [ yearStr, monthStr ] = month.split('-');
                                    const label = new CalendarDate(Number(yearStr), Number(monthStr), 1)
                                      .toDate(campaignTimezone).toLocaleDateString(LOCALE, { year: 'numeric', month: 'short', timeZone: campaignTimezone });
                                    const showError = month === errorAfterMonth && field.state.meta.errors.length > 0;
                                    const isPastMonth = month < currentMonth;

                                    return (
                                      <NumberField
                                        formatOptions={{ minimumFractionDigits: 2, maximumFractionDigits: 2 }}
                                        id={`monthlyBudget-${month}`}
                                        isDisabled={isPastMonth}
                                        isInvalid={showError}
                                        key={month}
                                        minValue={0}
                                        name={`monthlyBudget[${month}]`}
                                        size={48}
                                        style={renderProps.fieldStyles}
                                        value={field.state.value[month] ?? 0}
                                        onChange={(value) => {
                                          field.handleChange({
                                            ...field.state.value,
                                            [month]: value,
                                          });
                                        }}>
                                        <Label color={renderProps.labelColor}>{label}</Label>
                                        <View style={renderProps.rowViewStyle}>
                                          <Input placeholder="0.00" style={renderProps.narrowInputStyle}>
                                            {(inputRenderProps) =>
                                              <>
                                                {inputRenderProps.defaultChildren}
                                                <Text color="disabled">USD</Text>
                                              </>}
                                          </Input>
                                          {cost != null && cost > 0 && (
                                            <Hint>
                                              <Text color="quaternary" size={14} slot={null!}>{Math.floor((field.state.value[month] ?? 0) / cost).toLocaleString(LOCALE)}</Text>
                                              <Text color="placeholder" size={14} slot={null!}>Clicks</Text>
                                            </Hint>
                                          )}
                                        </View>
                                        {showError &&
                                      <Hint style={renderProps.underFieldStyle}>
                                        <Icon />
                                        {field.state.meta.errors.at(0)?.message}
                                      </Hint>}
                                      </NumberField>
                                    );
                                  });
                                }}
                              </form.Field>
                            );
                          }}
                        </form.Subscribe>
                      )}
                    </>
                  )}

                  {selectedType === 'pause' && (
                    <form.Field
                      name="pauseReason"
                      validators={{ onBlur: vPipe(vString(), vNonEmpty('Enter a reason to pause the campaign'), vMaxLength(150, 'Must be 150 characters or less')) }}>
                      {(field) => (
                        <TextField
                          id={field.name}
                          isInvalid={field.state.meta.errors.length > 0}
                          name={field.name}
                          style={renderProps.fieldStyles}
                          value={field.state.value}
                          onBlur={field.handleBlur}
                          onChange={field.handleChange}>
                          <Label color={renderProps.labelColor}>Reason</Label>
                          <TextArea />
                          {field.state.meta.errors.length > 0 &&
                        <Hint style={renderProps.underFieldStyle}>
                          <Icon />
                          {field.state.meta.errors.at(0)?.message}
                        </Hint>}
                        </TextField>
                      )}
                    </form.Field>
                  )}
                </>
              )}
            </form.Subscribe>

            <form.Subscribe
              selector={(state) => ({
                type: state.values.type,
                addBudget: state.values.addBudget,
                endDate: state.values.endDate,
                monthlyBudget: state.values.monthlyBudget,
                pauseReason: state.values.pauseReason,
                isSubmitting: state.isSubmitting,
              })}>
              {({ type, addBudget, endDate, monthlyBudget, pauseReason, isSubmitting }) => {
                const newTotal = (currentBudget || 0) + (addBudget || 0);
                const monthlyBudgetSum = isCustomMonthly
                  ? Object.values(monthlyBudget).reduce((acc, v) => acc + v, 0)
                  : 0;
                const monthlyBudgetRemaining = newTotal - monthlyBudgetSum;

                const hasEffectiveChanges = type === 'pause'
                  ? pauseReason.trim().length > 0
                  : (newTotal !== currentBudget)
                    || (endDate !== currentEndDate)
                    || (isCustomMonthly && !rIsDeepEqual(monthlyBudget, campaignQuery.data?.transformed.forForm?.monthlyBudget ?? {}));

                return (
                  <View slot={FORM_SLOTS.ACTIONS_VIEW}>
                    <Button
                      isDisabled={!hasEffectiveChanges || (isCustomMonthly && type === 'edit' && monthlyBudgetRemaining !== 0)}
                      isPending={isSubmitting}
                      type="submit">
                      {isCustomMonthly && type === 'edit' && monthlyBudgetRemaining !== 0
                        ? (monthlyBudgetRemaining < 0
                          ? `${Math.abs(monthlyBudgetRemaining).toFixed(2)} USD distributed over the budget`
                          : `${monthlyBudgetRemaining.toFixed(2)} USD to distribute`)
                        : 'Submit'}
                    </Button>
                    {inReviewEditRequest && (
                      <Button
                        isDisabled={isSubmitting || cancelMutation.isPending}
                        variant="quaternary"
                        onPress={async () => {
                          const response = await tryCatch(
                            minDelay(cancelMutation.mutateAsync({ id: inReviewEditRequest.original.id }), PROGRESS_INDICATOR_MIN_DELAY_DURATION),
                          );

                          if (response.error) {
                            routeContext.toastQueue.add(
                              { message: response.error.message, variant: 'error' },
                              { timeout: TOAST_TIMEOUT },
                            );
                            return;
                          }

                          routeContext.queryClient.invalidateQueries(campaignEditRequestQueries.all);
                          routeContext.queryClient.invalidateQueries(campaignRequestQueries.all);
                          routeContext.queryClient.invalidateQueries(auditLogQueries.all);

                          routeContext.toastQueue.add(
                            { message: 'Edit request cancelled.', variant: 'error' },
                            { timeout: TOAST_TIMEOUT },
                          );

                          clearDraft();
                          navigate({ to: '/campaigns/$campaignId', params: { campaignId: routeParams.campaignId }});
                        }}>
                      Cancel
                      </Button>
                    )}
                  </View>
                );
              }}
            </form.Subscribe>
          </>
        )}
      </Form>
    </>
  );
}

function ActionsComponent (): React.ReactElement {
  const routeParams = Route.useParams();

  return (
    <SecondaryActionsIconButton id={routeParams.campaignId}>
      <RouterMenuItemLink to="/campaigns/edit/guide">
        <HelpCircleStrokeIcon />
        <Text>Help</Text>
      </RouterMenuItemLink>
    </SecondaryActionsIconButton>
  );
}

function TopBar() {
  const routeParams = Route.useParams();
  const detailQuery = useQuery(campaignRequestQueries.detail(routeParams.campaignId));

  return (
    <Breadcrumbs>
      <Breadcrumb>
        <RouterLink to="/campaigns">Campaigns</RouterLink>
      </Breadcrumb>
      <Breadcrumb>
        <RouterLink params={routeParams} style={breadcrumbStyles.dynamic} to="/campaigns/$campaignId">{detailQuery.data?.original?.campaignName}</RouterLink>
      </Breadcrumb>
      <Breadcrumb hidden>
        <RouterLink params={routeParams} to="/campaigns/$campaignId/edit-requests">Edit requests</RouterLink>
      </Breadcrumb>
    </Breadcrumbs>
  );
}

const breadcrumbStyles = stylex.create({
  dynamic: {
    display: 'block',
    maxWidth: 200,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
});

const pageStyles = stylex.create({
  base: {
    minWidth: 461,
    paddingTop: 8,
  },
});

const segmentedButtonStyles = stylex.create({
  base: {
    width: 176,
  },
});

const labelWithHelperButtonStyles = stylex.create({
  base: {
    alignItems: 'center',
    display: 'flex',
    gap: 4,
  },
});

const helperButtonStyles = stylex.create({
  base: {
    backgroundColor: 'transparent',
    borderStyle: 'none',
    padding: 0,
  },
});
